import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Toolbar,
  InputAdornment,
  Divider,
  FormGroup,
  FormLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  PersonOff as PersonOffIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { extractErrorMessage, normalizeError, getFieldErrorMap } from '../../utils/errorHandler';
import { formatDateJST } from '../../utils/dateUtils';
import RequiredFieldLabel from '../../components/Common/RequiredFieldLabel';

// ==============================
// 型定義
// ==============================

interface User {
  employee_id: string;
  name: string;
  email: string;
  password_hash?: string;
  hire_date?: string;
  is_active: boolean;
  retired_at?: string;
  retired_by?: string;
  retire_reason?: string;
  roles?: string[];
  created_at?: string;
  updated_at?: string;
}

// ==============================
// メインコンポーネント
// ==============================

const UserManagementPage: React.FC = () => {
  // AuthContext から現在のユーザー情報を取得
  const { user: currentUser } = useAuth();
  const currentUserRoles = currentUser?.roles || [];

  // ステート管理
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // ダイアログの状態
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [retireDialogOpen, setRetireDialogOpen] = useState(false);

  // フォームの状態
  const [formData, setFormData] = useState<User>({
    employee_id: '',
    name: '',
    email: '',
    is_active: true,
    roles: ['employee'],
  });
  const [password, setPassword] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  // フォームのフィールド別エラー（フォームUX_バリデーション共通設計方針 §4, §6）
  const [userErrors, setUserErrors] = useState<Record<string, string>>({});

  // サーバのバリデーションエラーをフィールド直下へ反映し、トーストは要約のみ表示する
  const applyUserApiError = (error: unknown, defaultMessage: string) => {
    const fieldMap = getFieldErrorMap(error);
    if (Object.keys(fieldMap).length > 0) {
      setUserErrors(fieldMap);
      toast.error(normalizeError(error, defaultMessage).message);
    } else {
      toast.error(extractErrorMessage(error, defaultMessage), {
        autoClose: 8000,
      });
    }
  };

  // 退職フォームの状態
  const [retireDate, setRetireDate] = useState<string>((() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })());
  const [retireReason, setRetireReason] = useState<string>('');

  // CSV一括登録の状態
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [importErrors, setImportErrors] = useState<{ row: number; employee_id: string; message: string }[]>([]);

  // ==============================
  // データ取得
  // ==============================

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      // /employees/simpleは全従業員を返す（limit制限なし）
      const response = await api.get('/employees/simple', {
        params: {
          include_inactive: true // 無効なユーザーも含む
        }
      });

      // すべてのユーザーを取得（無効なユーザーも含む）
      if (response.data.success) {
        setUsers(response.data.data || []);
      }
    } catch (err: any) {
      console.error('ユーザー取得エラー:', err);
      setError(err.response?.data?.message || 'ユーザーの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // ==============================
  // ユーザー作成
  // ==============================

  const handleOpenCreateDialog = () => {
    setFormData({
      employee_id: '',
      name: '',
      email: '',
      hire_date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(),
      is_active: true,
      roles: ['employee'],
    });
    setPassword('');
    setUserErrors({});
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  const handleCreateUser = async () => {
    const errors: Record<string, string> = {};
    if (!formData.employee_id) errors.employee_id = '社員IDを入力してください';
    if (!formData.name) errors.name = '氏名を入力してください';
    if (!formData.email) errors.email = 'メールアドレスを入力してください';
    if (!formData.hire_date) errors.hire_date = '入社日を入力してください';
    if (!password) errors.password = 'パスワードを入力してください';
    setUserErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error('入力内容を確認してください');
      return;
    }

    try {
      await api.post('/employees/basic', {
        employee_id: formData.employee_id,
        name: formData.name,
        email: formData.email,
        password: password,
        hire_date: formData.hire_date,
        roles: formData.roles || ['employee'],
        // 部門・職位情報は後から「所属・職位管理」で設定
      });

      toast.success('ユーザーを作成しました。所属・職位は「所属・職位管理」で設定してください。');
      await fetchUsers();
      handleCloseCreateDialog();
    } catch (err: any) {
      console.error('ユーザー作成エラー:', err);
      applyUserApiError(err, 'エラーが発生しました');
    }
  };

  // ==============================
  // ユーザー編集
  // ==============================

  const handleOpenEditDialog = (user: User) => {
    setSelectedUser(user);
    // hire_dateが存在し、かつISO形式（YYYY-MM-DDTHH:mm:ss.sssZ）の場合はYYYY-MM-DDに変換
    const formattedHireDate = user.hire_date
      ? user.hire_date.split('T')[0]
      : (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();

    setFormData({
      employee_id: user.employee_id,
      name: user.name,
      email: user.email,
      hire_date: formattedHireDate,
      is_active: user.is_active,
      roles: user.roles || ['employee'],
    });
    setPassword('');
    setUserErrors({});
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
    setUserErrors({});
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = '氏名を入力してください';
    if (!formData.email) errors.email = 'メールアドレスを入力してください';
    if (!formData.hire_date) errors.hire_date = '入社日を入力してください';
    setUserErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error('入力内容を確認してください');
      return;
    }

    try {
      const updateData: any = {
        name: formData.name,
        email: formData.email,
        hire_date: formData.hire_date,
        is_active: formData.is_active,
        roles: formData.roles,
      };

      // パスワードが入力されている場合のみ更新
      if (password) {
        updateData.password = password;
      }

      await api.put(`/employees/basic/${selectedUser.employee_id}`, updateData);

      toast.success('ユーザー情報を更新しました');
      await fetchUsers();
      handleCloseEditDialog();
    } catch (err: any) {
      console.error('ユーザー更新エラー:', err);
      applyUserApiError(err, 'エラーが発生しました');
    }
  };

  // ==============================
  // ユーザー削除
  // ==============================

  const handleDeleteUser = async (employeeId: string) => {
    if (!window.confirm('このユーザーを無効化してもよろしいですか？')) {
      return;
    }

    try {
      await api.patch(`/employees/${employeeId}/deactivate`, {});

      toast.success('ユーザーを無効化しました');
      await fetchUsers();
    } catch (err: any) {
      console.error('ユーザー無効化エラー:', err);
      toast.error(err.response?.data?.message || 'ユーザーの無効化に失敗しました');
    }
  };

  // ==============================
  // 退職処理
  // ==============================

  const handleOpenRetireDialog = (user: User) => {
    setSelectedUser(user);
    setRetireDate((() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })());
    setRetireReason('');
    setRetireDialogOpen(true);
  };

  const handleCloseRetireDialog = () => {
    setRetireDialogOpen(false);
    setSelectedUser(null);
    setRetireDate((() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })());
    setRetireReason('');
  };

  const handleRetire = async () => {
    if (!selectedUser) return;

    if (!retireDate) {
      toast.error('退職日を入力してください');
      return;
    }

    if (!retireReason.trim()) {
      toast.error('退職理由を入力してください');
      return;
    }

    // 確認ダイアログ
    if (!window.confirm(
      `社員「${selectedUser.name}」を退職処理します。\n\n` +
      `退職日: ${formatDateJST(retireDate)}\n` +
      `理由: ${retireReason}\n\n` +
      `この操作は取り消しできません。本当に実行しますか？`
    )) {
      return;
    }

    try {
      await api.post(`/employees/${selectedUser.employee_id}/retire`, {
        retire_date: retireDate,
        retire_reason: retireReason
      });

      toast.success('退職処理が完了しました');
      await fetchUsers();
      handleCloseRetireDialog();
    } catch (err: any) {
      console.error('退職処理エラー:', err);
      toast.error(err.response?.data?.message || '退職処理に失敗しました');
    }
  };

  // ==============================
  // 有効/無効切り替え
  // ==============================

  const handleToggleActive = async (employeeId: string, currentActive: boolean) => {
    const action = currentActive ? '無効化' : '有効化';

    if (!window.confirm(`このユーザーを${action}してもよろしいですか？`)) {
      return;
    }

    try {
      if (currentActive) {
        await api.patch(`/employees/${employeeId}/deactivate`, {});
      } else {
        await api.patch(`/employees/${employeeId}/reactivate`, {});
      }

      toast.success(`ユーザーを${action}しました`);
      await fetchUsers();
    } catch (err: any) {
      console.error(`ユーザー${action}エラー:`, err);
      toast.error(err.response?.data?.message || `ユーザーの${action}に失敗しました`);
    }
  };

  // ==============================
  // データエクスポート（CSV）
  // ==============================

  const handleExportData = () => {
    const headers = ['employee_id', 'name', 'email', 'hire_date', 'password', 'role', 'is_active'];

    // 現在のユーザーデータをエクスポート（passwordは空欄）
    const dataRows = users
      .filter(user => user.employee_id !== 'system') // システムユーザーは除外
      .map(user => {
        const hireDate = user.hire_date ? user.hire_date.split('T')[0] : '';
        const roles = user.roles?.join(';') || 'employee'; // 複数ロールは;区切り
        return [
          user.employee_id,
          user.name,
          user.email,
          hireDate,
          '', // パスワードは空欄（セキュリティのため）
          roles,
          user.is_active ? 'true' : 'false'
        ];
      });

    const csvContent = [headers.join(','), ...dataRows.map((row) => row.join(','))].join('\n');

    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `users_export_${(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`${dataRows.length}件のユーザーデータをエクスポートしました`);
  };

  // ==============================
  // CSVアップロード
  // ==============================

  const handleCsvFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('CSVファイルを選択してください');
        return;
      }
      setCsvFile(file);
    }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      toast.error('CSVファイルを選択してください');
      return;
    }

    setCsvUploading(true);
    setImportErrors([]); // エラーをリセット

    try {
      const formData = new FormData();
      formData.append('csv_file', csvFile);

      const response = await api.post('/employees/import-csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        const { created_count, error_count, errors } = response.data.data;

        if (error_count > 0) {
          // エラーを状態に保存してUIに表示
          const formattedErrors = (errors || []).map((err: any, index: number) => ({
            row: err.row || index + 2, // ヘッダー行 + 1
            employee_id: err.employee_id || '-',
            message: err.message || err.error || 'エラー'
          }));
          setImportErrors(formattedErrors);
          toast.warning(
            `${created_count}件作成、${error_count}件エラー。下記のエラー詳細を確認してください。`,
            { autoClose: 5000 }
          );
        } else {
          toast.success(`${created_count}件のユーザーを作成しました`);
          setBulkDialogOpen(false);
          setCsvFile(null);
        }

        await fetchUsers();
      }
    } catch (err: any) {
      console.error('CSVアップロードエラー:', err);
      toast.error(err.response?.data?.message || 'CSVアップロードに失敗しました');
    } finally {
      setCsvUploading(false);
    }
  };

  // ==============================
  // フィルタリング
  // ==============================

  const filteredUsers = users.filter(
    (user) =>
      user.employee_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ==============================
  // ロール表示用
  // ==============================

  const ROLE_LABELS: Record<string, string> = {
    system_supervisor: 'システム管理者',
    hr: 'HR管理者',
    skill_supervisor: 'スキル管理者',
    sales_supervisor: '営業管理者',
    employee: '社員',
  };

  const ROLE_COLORS: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'default'> = {
    system_supervisor: 'secondary',
    hr: 'primary',
    skill_supervisor: 'success',
    sales_supervisor: 'warning',
    employee: 'default',
  };

  // ログインユーザーのロールに基づいて利用可能なロールを動的に計算
  // 複数ロールを持つ場合は、各ロールで設定可能なロールをマージする
  const getAvailableRoles = () => {
    const allRoles = [
      { value: 'system_supervisor', label: 'システム管理者' },
      { value: 'hr', label: 'HR管理者' },
      { value: 'skill_supervisor', label: 'スキル管理者' },
      { value: 'sales_supervisor', label: '営業管理者' },
      { value: 'employee', label: '社員' },
    ];

    // 設定可能なロール値を集めるSet
    const availableRoleValues = new Set<string>();

    // system_supervisor の場合: hr, employee を追加
    if (currentUserRoles.includes('system_supervisor')) {
      ['hr', 'employee'].forEach(r => availableRoleValues.add(r));
    }

    // hr の場合: sales_supervisor, skill_supervisor, employee を追加
    if (currentUserRoles.includes('hr')) {
      ['sales_supervisor', 'skill_supervisor', 'employee'].forEach(r => availableRoleValues.add(r));
    }

    // いずれのロールにも該当しない場合は employee のみ
    if (availableRoleValues.size === 0) {
      availableRoleValues.add('employee');
    }

    return allRoles.filter(role => availableRoleValues.has(role.value));
  };

  const AVAILABLE_ROLES = useMemo(() => getAvailableRoles(), [currentUserRoles]);

  const getRoleChips = (roles?: string[]) => {
    if (!roles || roles.length === 0) {
      return [{ label: ROLE_LABELS.employee, color: ROLE_COLORS.employee }];
    }
    return roles.map(role => ({
      label: ROLE_LABELS[role] || role,
      color: ROLE_COLORS[role] || 'default' as const
    }));
  };

  const handleRoleChange = (role: string, checked: boolean) => {
    const currentRoles = formData.roles || [];
    let newRoles: string[];

    if (checked) {
      // ロールを追加
      newRoles = [...currentRoles, role];
    } else {
      // ロールを削除
      newRoles = currentRoles.filter(r => r !== role);
    }

    // 最低1つのロールは必要
    if (newRoles.length === 0) {
      newRoles = ['employee'];
    }

    setFormData({ ...formData, roles: newRoles });
  };

  // ==============================
  // レンダリング
  // ==============================

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          ユーザー管理
        </Typography>
        <Typography variant="body2" color="text.secondary">
          社員の基本情報とロール（システム管理者、HR管理者、スキル管理者、営業管理者、社員）を管理します
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          ※ユーザーには複数のロールを付与できます。無効化されたユーザーも表示されます。
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ツールバー */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Toolbar sx={{ px: { xs: 0 }, justifyContent: 'space-between' }}>
          <TextField
            size="small"
            placeholder="社員ID、氏名、メールで検索..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportData}
            >
              データエクスポート
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => setBulkDialogOpen(true)}
            >
              一括登録
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchUsers}>
              更新
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDialog}
            >
              新規
            </Button>
          </Box>
        </Toolbar>
      </Paper>

      {/* ユーザー一覧テーブル */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>社員ID</TableCell>
                <TableCell>氏名</TableCell>
                <TableCell>メールアドレス</TableCell>
                <TableCell>入社日</TableCell>
                <TableCell align="center">ロール</TableCell>
                <TableCell align="center">状態</TableCell>
                <TableCell align="center">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">ユーザーが見つかりません</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.employee_id} hover>
                    <TableCell>{user.employee_id}</TableCell>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {user.hire_date ? formatDateJST(user.hire_date) : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {getRoleChips(user.roles).map((chip, index) => (
                          <Chip
                            key={index}
                            label={chip.label}
                            size="small"
                            color={chip.color}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {user.retired_at ? (
                        <Chip
                          label={`退職 (${formatDateJST(user.retired_at)})`}
                          size="small"
                          sx={{ bgcolor: 'grey.400', color: 'white' }}
                        />
                      ) : user.is_active ? (
                        <Chip
                          label="有効"
                          size="small"
                          color="success"
                        />
                      ) : (
                        <Chip
                          label="無効"
                          size="small"
                          color="warning"
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenEditDialog(user)}
                        disabled={user.employee_id === 'system'}
                        title={user.employee_id === 'system' ? 'システムユーザーは変更できません' : '編集'}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {!user.retired_at && (
                        <>
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleOpenRetireDialog(user)}
                            title="退職処理"
                            disabled={user.employee_id === 'system'}
                          >
                            <PersonOffIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color={user.is_active ? 'default' : 'success'}
                            onClick={() => handleToggleActive(user.employee_id, user.is_active)}
                            title={user.employee_id === 'system' ? 'システムユーザーは変更できません' : (user.is_active ? '無効化' : '有効化')}
                            disabled={user.employee_id === 'system'}
                          >
                            {user.is_active ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ==============================
          新規作成ダイアログ
          ============================== */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>ユーザー新規作成</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <RequiredFieldLabel label="社員ID" sx={{ mt: 2 }} />
            <TextField
              fullWidth
              value={formData.employee_id}
              onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
              error={!!userErrors.employee_id}
              helperText={userErrors.employee_id}
            />
            <RequiredFieldLabel label="氏名" sx={{ mt: 2 }} />
            <TextField
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={!!userErrors.name}
              helperText={userErrors.name}
            />
            <RequiredFieldLabel label="メールアドレス" sx={{ mt: 2 }} />
            <TextField
              fullWidth
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={!!userErrors.email}
              helperText={userErrors.email}
            />
            <RequiredFieldLabel label="入社日" sx={{ mt: 2 }} />
            <TextField
              fullWidth
              type="date"
              value={formData.hire_date || ''}
              onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
              error={!!userErrors.hire_date}
              helperText={userErrors.hire_date}
              InputLabelProps={{ shrink: true }}
            />
            <RequiredFieldLabel label="パスワード" sx={{ mt: 2 }} />
            <TextField
              fullWidth
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!userErrors.password}
              helperText={userErrors.password}
            />
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                ロール（複数選択可）
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {AVAILABLE_ROLES.map((role) => {
                  const isSelected = formData.roles?.includes(role.value) || false;
                  return (
                    <Chip
                      key={role.value}
                      label={role.label}
                      onClick={() => handleRoleChange(role.value, !isSelected)}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: isSelected ? 'primary.dark' : 'action.hover'
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="有効"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>キャンセル</Button>
          <Button onClick={handleCreateUser} variant="contained" color="primary">
            作成
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==============================
          編集ダイアログ
          ============================== */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>ユーザー編集</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="社員ID"
                value={formData.employee_id}
                disabled
                margin="normal"
              />
              <RequiredFieldLabel label="氏名" sx={{ mt: 2 }} />
              <TextField
                fullWidth
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                error={!!userErrors.name}
                helperText={userErrors.name}
              />
              <RequiredFieldLabel label="メールアドレス" sx={{ mt: 2 }} />
              <TextField
                fullWidth
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                error={!!userErrors.email}
                helperText={userErrors.email}
              />
              <RequiredFieldLabel label="入社日" sx={{ mt: 2 }} />
              <TextField
                fullWidth
                type="date"
                value={formData.hire_date || ''}
                onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                error={!!userErrors.hire_date}
                helperText={userErrors.hire_date}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="新しいパスワード（変更する場合のみ）"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                helperText="パスワードを変更しない場合は空欄のままにしてください"
              />
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  ロール（複数選択可）
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {AVAILABLE_ROLES.map((role) => {
                    const isSelected = formData.roles?.includes(role.value) || false;
                    return (
                      <Chip
                        key={role.value}
                        label={role.label}
                        onClick={() => handleRoleChange(role.value, !isSelected)}
                        color={isSelected ? 'primary' : 'default'}
                        variant={isSelected ? 'filled' : 'outlined'}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: isSelected ? 'primary.dark' : 'action.hover'
                          }
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="有効"
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>キャンセル</Button>
          <Button onClick={handleUpdateUser} variant="contained" color="primary">
            更新
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==============================
          一括登録ダイアログ
          ============================== */}
      <Dialog open={bulkDialogOpen} onClose={() => { setBulkDialogOpen(false); setImportErrors([]); setCsvFile(null); }} maxWidth="md" fullWidth>
        <DialogTitle>ユーザー一括登録（CSVインポート）</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              CSVファイルからユーザーを一括登録できます。まずテンプレートをダウンロードして、必要事項を記入してからアップロードしてください。
            </Alert>

            {/* ステップ1: テンプレートダウンロード */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                ステップ1: 現在のデータをエクスポート
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                現在の社員データをCSVファイルでエクスポートします。このファイルを編集してインポートすることもできます。
              </Typography>
              <Button variant="outlined" onClick={handleExportData} startIcon={<DownloadIcon />}>
                データをエクスポート
              </Button>
            </Paper>

            {/* ステップ2: CSVアップロード */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                ステップ2: 記入済みCSVをアップロード
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                社員ID、氏名、メール、パスワード、ロール、状態を記入したCSVファイルをアップロードします。
              </Typography>

              <Box sx={{ mb: 2 }}>
                <input
                  accept=".csv"
                  style={{ display: 'none' }}
                  id="csv-file-upload-bulk"
                  type="file"
                  onChange={handleCsvFileChange}
                />
                <label htmlFor="csv-file-upload-bulk">
                  <Button variant="outlined" component="span">
                    CSVファイルを選択
                  </Button>
                </label>
                {csvFile && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    選択されたファイル: {csvFile.name}
                  </Typography>
                )}
              </Box>

              {csvFile && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  注意: アップロードすると、CSVに記載された全てのユーザーが作成されます。重複する社員IDはスキップされます。
                </Alert>
              )}
            </Paper>

            {/* インポートエラー表示 */}
            {importErrors.length > 0 && (
              <Paper sx={{ p: 2, mt: 3 }}>
                <Typography variant="subtitle1" color="error" gutterBottom>
                  インポートエラー ({importErrors.length}件)
                </Typography>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>行番号</TableCell>
                        <TableCell>社員ID</TableCell>
                        <TableCell>エラー内容</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importErrors.map((err, index) => (
                        <TableRow key={index}>
                          <TableCell>{err.row}</TableCell>
                          <TableCell>{err.employee_id}</TableCell>
                          <TableCell sx={{ color: 'error.main' }}>{err.message}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBulkDialogOpen(false); setImportErrors([]); setCsvFile(null); }} disabled={csvUploading}>
            キャンセル
          </Button>
          <Button
            onClick={handleCsvUpload}
            variant="contained"
            color="primary"
            disabled={!csvFile || csvUploading}
            startIcon={csvUploading ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            {csvUploading ? 'アップロード中...' : 'アップロードして作成'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==============================
          退職処理ダイアログ
          ============================== */}
      <Dialog open={retireDialogOpen} onClose={handleCloseRetireDialog} maxWidth="sm" fullWidth>
        <DialogTitle>退職処理</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning" sx={{ mb: 3 }}>
                退職処理は取り消しできません。この社員は退職済みとしてマークされ、システムから論理削除されます。
              </Alert>

              <TextField
                fullWidth
                label="社員ID"
                value={selectedUser.employee_id}
                disabled
                margin="normal"
              />
              <TextField
                fullWidth
                label="氏名"
                value={selectedUser.name}
                disabled
                margin="normal"
              />
              <TextField
                fullWidth
                label="退職日"
                type="date"
                value={retireDate}
                onChange={(e) => setRetireDate(e.target.value)}
                margin="normal"
                required
                InputLabelProps={{ shrink: true }}
                helperText="退職した日付を入力してください"
              />
              <TextField
                fullWidth
                label="退職理由"
                value={retireReason}
                onChange={(e) => setRetireReason(e.target.value)}
                margin="normal"
                required
                multiline
                rows={3}
                helperText="退職の理由を入力してください（例: 自己都合、定年退職、会社都合など）"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRetireDialog}>キャンセル</Button>
          <Button onClick={handleRetire} variant="contained" color="warning">
            退職処理を実行
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagementPage;
